import { ResponseBuilder } from "../core/ResponseBuilder.js";
import type { AssistantResponse } from "../types/AssistantResponse.js";
import type { MountainContext } from "../types/MountainContext.js";

export interface SafetyRuleConfig {
  readonly poorGpsAccuracyMeters: number;
  readonly warningRouteDeviationMeters: number;
}

const DEFAULT_CONFIG: SafetyRuleConfig = {
  poorGpsAccuracyMeters: 50,
  warningRouteDeviationMeters: 150,
};

export class MountainSafetyRules {
  constructor(private readonly config: SafetyRuleConfig = DEFAULT_CONFIG) {}

  evaluate(context: MountainContext): AssistantResponse | null {
    const criticalAlert = (context.alerts ?? []).find((alert) => alert.level === "CRITICAL");
    if (criticalAlert) {
      return ResponseBuilder.build({
        message: criticalAlert.message,
        severity: "CRITICAL",
        confidence: 1,
        dataUsed: ["alerts"],
        dataTimestamp: criticalAlert.timestamp,
        offline: context.offline,
        recommendedAction: "Detente y prioriza una decision conservadora. Regresa o busca una alternativa segura si las condiciones lo permiten.",
        limitations: ["Esta alerta no garantiza rescate ni comunicacion de emergencia."],
        source: "SAFETY_RULE",
      });
    }

    if (context.gps && context.gps.accuracyMeters > this.config.poorGpsAccuracyMeters) {
      return ResponseBuilder.build({
        message: "La precision actual del GPS es insuficiente para una orientacion fiable.",
        severity: "CAUTION",
        confidence: 0.95,
        dataUsed: ["gps.accuracyMeters"],
        dataTimestamp: context.gps.timestamp,
        offline: context.offline,
        recommendedAction: "Detente y espera una mejor precision antes de tomar decisiones de navegacion dependientes de la posicion.",
        limitations: ["La posicion puede contener un error significativo."],
        source: "SAFETY_RULE",
      });
    }

    if (
      context.route?.routeDeviationMeters !== null &&
      context.route?.routeDeviationMeters !== undefined &&
      context.route.routeDeviationMeters >= this.config.warningRouteDeviationMeters
    ) {
      return ResponseBuilder.build({
        message: "La posicion simulada presenta una desviacion relevante respecto de la ruta.",
        severity: "WARNING",
        confidence: 0.9,
        dataUsed: ["route.routeDeviationMeters"],
        dataTimestamp: context.route.timestamp,
        offline: context.offline,
        recommendedAction: "Detente, verifica posicion y ruta, y considera regresar al ultimo punto confirmado antes de continuar.",
        limitations: ["El umbral usado en V1 es experimental y requiere validacion posterior en Work y campo."],
        source: "SAFETY_RULE",
      });
    }

    return null;
  }
}
